import React, { useState, useEffect, useRef } from 'react';
import { MdClose, MdSend, MdPerson, MdGroup, MdSearch, MdAttachFile } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';
import socketService from '../../services/socketService';


const ChatModal = ({ isOpen, onClose, user, userRole }) => {
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const messagesEndRef = useRef(null);

  // API configuration is now imported from config/api.js

  useEffect(() => {
    if (isOpen) {
      fetchContacts();
      fetchUnreadCounts();

      // Connect to socket when chat opens
      if (user) {
        socketService.connect({
          user_id: user.user_id,
          role: userRole,
          mmda_id: user.mmda_id
        });
      }
    }
  }, [isOpen, user, userRole]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Socket.IO event listeners
  useEffect(() => {
    if (!isOpen) return;

    const handleNewMessage = (message, isSent = false) => {
      if (activeChat && (
        (message.sender_id === activeChat.user_id || message.sender_id === activeChat.id) ||
        (message.receiver_id === activeChat.user_id || message.receiver_id === activeChat.id)
      )) {
        // Update messages if this is for the active chat
        setMessages(prev => {
          // Avoid duplicates
          const exists = prev.some(m => m.message_id === message.message_id);
          if (!exists) {
            return [...prev, message];
          }
          return prev;
        });

        // Mark as read if we received it
        if (!isSent && message.receiver_id === user.user_id) {
          markAsRead(message.sender_id);
        }
      }

      // Update unread counts
      if (!isSent) {
        fetchUnreadCounts();
      }
    };

    const handleUserStatus = (userId, isOnline) => {
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        if (isOnline) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    };

    const removeMessageListener = socketService.addMessageListener(handleNewMessage);
    const removeStatusListener = socketService.addStatusListener(handleUserStatus);

    return () => {
      removeMessageListener();
      removeStatusListener();
    };
  }, [isOpen, activeChat, user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchContacts = async () => {
    try {
      setLoading(true);
      let endpoint = '';

      // All users can chat with others in their MMDA
      endpoint = `/messages/contacts/${user.user_id}?role=${userRole}`;

      const response = await axios.get(`${API_BASE_URL}${endpoint}`, API_CONFIG);
      setContacts(response.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      // Show user-friendly error message
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/messages/unread/${user.user_id}`, API_CONFIG);
      const counts = {};
      response.data.unread_by_sender.forEach(item => {
        counts[item.sender_id] = item.unread_count;
      });
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  const markAsRead = async (senderId) => {
    try {
      await axios.put(`${API_BASE_URL}/messages/read`, {
        sender_id: senderId,
        receiver_id: user.user_id
      }, API_CONFIG);

      // Update unread counts
      setUnreadCounts(prev => ({
        ...prev,
        [senderId]: 0
      }));
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const fetchMessages = async (contactId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/messages?user1=${user.user_id}&user2=${contactId}`,
        API_CONFIG
      );
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      if (error.response?.status === 403) {
        alert('Cannot access messages from users in different MMDA');
      }
      setMessages([]);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChat) return;

    try {
      const messageData = {
        sender_id: user.user_id,
        receiver_id: activeChat.user_id || activeChat.id,
        message: newMessage.trim(),
        timestamp: new Date().toISOString()
      };

      // Send via HTTP API (which will also emit via Socket.IO)
      const response = await axios.post(`${API_BASE_URL}/messages`, messageData, API_CONFIG);

      if (response.status === 201) {
        // Message will be added via Socket.IO event, but add immediately for better UX
        setMessages(prev => {
          const exists = prev.some(m => m.message_id === response.data.message_id);
          if (!exists) {
            return [...prev, response.data];
          }
          return prev;
        });
        setNewMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      if (error.response?.status === 403) {
        alert('Cannot send message to user in different MMDA');
      } else if (error.response?.status === 404) {
        alert('User not found');
      } else {
        alert('Failed to send message. Please try again.');
      }
    }
  };

  const handleContactSelect = (contact) => {
    setActiveChat(contact);
    fetchMessages(contact.user_id || contact.id);

    // Mark messages as read when opening chat
    const contactId = contact.user_id || contact.id;
    if (unreadCounts[contactId] > 0) {
      markAsRead(contactId);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const fullName = contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    const searchLower = searchTerm.toLowerCase();
    return fullName.toLowerCase().includes(searchLower) ||
           contact.email?.toLowerCase().includes(searchLower) ||
           contact.role?.toLowerCase().includes(searchLower);
  });

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div className="modal-content" style={{
        width: '95%',
        maxWidth: '1200px',
        height: '85vh',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div className="modal-header" style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            display: 'flex',
            alignItems: 'center'
          }}>
            <MdGroup style={{ marginRight: '12px', fontSize: '24px', color: '#2196f3' }} />
            MMDA Chat
          </h2>
          <button
            className="close-button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f0f0f0'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <MdClose />
          </button>
        </div>

        <div style={{ display: 'flex', height: 'calc(100% - 70px)' }}>
          {/* Contacts Sidebar */}
          <div style={{
            width: '350px',
            borderRight: '1px solid #e0e0e0',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fafafa'
          }}>
            {/* Search Header */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}>
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
              }}>
                Contacts ({contacts.length})
              </h3>
              <div style={{ position: 'relative' }}>
                <MdSearch style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  fontSize: '18px'
                }} />
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '70%',
                    padding: '12px 12px 12px 40px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: '#f8f9fa',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#2196f3';
                    e.target.style.backgroundColor = 'white';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                />
              </div>
            </div>

            {/* Contacts List */}
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              {loading ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <div style={{ marginBottom: '10px' }}>🔄</div>
                  Loading contacts...
                </div>
              ) : filteredContacts.length === 0 ? (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: '#999'
                }}>
                  <div style={{ marginBottom: '10px' }}>👥</div>
                  {searchTerm ? 'No contacts match your search' : 'No contacts found'}
                </div>
              ) : (
                filteredContacts.map((contact) => {
                  const displayName = contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
                  const initials = displayName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2);
                  const isActive = activeChat?.user_id === contact.user_id;

                  // Clean up role name - remove underscores and capitalize properly
                  const cleanRole = (contact.role || 'Field Officer')
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());

                  // Determine role color
                  const roleColor = contact.role === 'Field Officer' || contact.role === 'field_officer' ? '#4caf50' : '#2196f3';

                  return (
                    <div
                      key={contact.user_id || contact.id}
                      onClick={() => handleContactSelect(contact)}
                      style={{
                        padding: '16px 20px',
                        cursor: 'pointer',
                        backgroundColor: isActive ? '#e3f2fd' : 'white',
                        borderLeft: isActive ? '4px solid #2196f3' : '4px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.3s ease',
                        borderBottom: '1px solid #f0f0f0',
                        position: 'relative'
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                          e.currentTarget.style.transform = 'translateX(2px)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = 'white';
                          e.currentTarget.style.transform = 'translateX(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        backgroundColor: roleColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '14px',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        transition: 'all 0.3s ease'
                      }}>
                        {initials || 'U'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: '500',
                            fontSize: '15px',
                            color: '#333',
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = roleColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#333';
                          }}
                        >
                          {displayName || 'Unknown User'}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#666',
                            display: 'flex',
                            alignItems: 'center',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = roleColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#666';
                          }}
                        >
                          <span style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: roleColor,
                            marginRight: '8px',
                            transition: 'all 0.2s ease'
                          }}></span>
                          {cleanRole}
                        </div>
                      </div>

                      {/* Unread message badge */}
                      {unreadCounts[contact.user_id || contact.id] > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          borderRadius: '10px',
                          minWidth: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                          {unreadCounts[contact.user_id || contact.id]}
                        </div>
                      )}

                      {/* Online status indicator */}
                      <div style={{
                        position: 'absolute',
                        top: unreadCounts[contact.user_id || contact.id] > 0 ? '36px' : '16px',
                        right: '16px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: onlineUsers.has(contact.user_id || contact.id) ? '#4caf50' : '#ccc',
                        border: '2px solid white',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)'
                      }}></div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {activeChat ? (
              <>
                {/* Chat Header */}
                <div style={{
                  padding: '20px 24px',
                  borderBottom: '1px solid #e0e0e0',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    backgroundColor: activeChat.role === 'Field Officer' ? '#4caf50' : '#2196f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '16px',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '16px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {(() => {
                      const displayName = activeChat.full_name || `${activeChat.first_name || ''} ${activeChat.last_name || ''}`.trim();
                      return displayName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2) || 'U';
                    })()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: '#333',
                      marginBottom: '2px'
                    }}>
                      {activeChat.full_name || `${activeChat.first_name || ''} ${activeChat.last_name || ''}`.trim() || 'Unknown User'}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#666',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <span style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: (activeChat.role === 'Field Officer' || activeChat.role === 'field_officer') ? '#4caf50' : '#2196f3',
                        marginRight: '6px'
                      }}></span>
                      {(activeChat.role || 'Field Officer').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} • {onlineUsers.has(activeChat.user_id || activeChat.id) ? 'Online' : 'Offline'}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  padding: '15px',
                  backgroundColor: '#f5f5f5'
                }}>
                  {messages.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      color: '#666', 
                      marginTop: '50px' 
                    }}>
                      No messages yet. Start the conversation!
                    </div>
                  ) : (
                    messages.map((message, index) => {
                      const isOwnMessage = message.sender_id === user.user_id;
                      const showDate = index === 0 || 
                        formatDate(message.timestamp) !== formatDate(messages[index - 1].timestamp);

                      return (
                        <div key={message.id || index}>
                          {showDate && (
                            <div style={{ 
                              textAlign: 'center', 
                              margin: '20px 0 10px',
                              fontSize: '12px',
                              color: '#666'
                            }}>
                              {formatDate(message.timestamp)}
                            </div>
                          )}
                          <div style={{
                            display: 'flex',
                            justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                            marginBottom: '10px'
                          }}>
                            <div style={{
                              maxWidth: '70%',
                              padding: '10px 15px',
                              borderRadius: '18px',
                              backgroundColor: isOwnMessage ? '#2196f3' : 'white',
                              color: isOwnMessage ? 'white' : 'black',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                              <div>{message.message}</div>
                              <div style={{
                                fontSize: '11px',
                                opacity: 0.7,
                                marginTop: '5px',
                                textAlign: 'right'
                              }}>
                                {formatTime(message.timestamp)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div style={{
                  padding: '20px 24px',
                  borderTop: '1px solid #e0e0e0',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your message..."
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        backgroundColor: 'transparent'
                      }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '10px',
                        border: 'none',
                        backgroundColor: newMessage.trim() ? '#2196f3' : '#e0e0e0',
                        color: newMessage.trim() ? 'white' : '#999',
                        cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginLeft: '8px',
                        transition: 'all 0.2s',
                        fontSize: '18px'
                      }}
                      onMouseOver={(e) => {
                        if (newMessage.trim()) {
                          e.target.style.backgroundColor = '#1976d2';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (newMessage.trim()) {
                          e.target.style.backgroundColor = '#2196f3';
                        }
                      }}
                    >
                      <MdSend />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                backgroundColor: '#fafafa'
              }}>
                <div style={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  opacity: 0.5
                }}>
                  💬
                </div>
                <h3 style={{
                  margin: '0 0 8px 0',
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#666'
                }}>
                  Welcome to MMDA Chat
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#999',
                  textAlign: 'center',
                  maxWidth: '300px'
                }}>
                  Select a contact from the sidebar to start a conversation with your colleagues
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
